package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.anjuros.website.redesign.theme.RdSize
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

/** Body text colour of articles: a touch softer than headings. */
private const val PROSE_TEXT = "rgb(59,69,82)"
private const val SKY_LINE = "1px solid rgb(180,198,220)"

// ---------- Reading progress ----------

val ArticleProgressStyle = CssStyle.base {
    Modifier.position(Position.Fixed).backgroundColor(RdColor.Brand)
        .css("left" to "0", "top" to "0", "height" to "3px", "width" to "0", "z-index" to 30, "margin-top" to "env(safe-area-inset-top, 0px)")
}

// ---------- Title block ----------

val ArticleHeadStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(20.px)
        .css("max-width" to "980px", "padding-block" to "clamp(32px, 5vw, 64px) clamp(24px, 3vw, 36px)")
}

val ArticleTitleStyle = CssStyle.base {
    Modifier.fontFamily(*RdFont.DISPLAY).css(
        "font-size" to "clamp(2.1rem, 1.1rem + 3.4vw, 3.6rem)", "font-weight" to 900, "font-stretch" to "116%",
        "line-height" to 1.02, "letter-spacing" to "-.02em", "max-width" to "22ch",
    )
}

val ArticleBylineStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).gap(12.px).color(RdColor.Muted).textDecorationLine(TextDecorationLine.None)
            .css("align-items" to "center", "font-size" to ".95rem", "width" to "fit-content")
    }
    cssRule(" img") {
        Modifier.backgroundColor(RdColor.SkySoft).borderRadius(50.px)
            .css("width" to "44px", "height" to "44px", "border" to SKY_LINE, "object-fit" to "cover", "object-position" to "50% 20%")
    }
    cssRule(" b") { Modifier.color(RdColor.Ink).css("font-weight" to 600) }
    cssRule(":hover b") { Modifier.color(RdColor.Brand) }
}

val ArticleCoverStyle = CssStyle {
    base {
        Modifier.borderRadius(20.px)
            .css("margin" to "0", "overflow" to "hidden", "aspect-ratio" to "4 / 3", "max-width" to "100%", "box-shadow" to RdSize.SHADOW)
    }
    Breakpoint.MD { Modifier.css("aspect-ratio" to "16 / 7") }
    cssRule(" img") { Modifier.display(DisplayStyle.Block).css("width" to "100%", "height" to "100%", "object-fit" to "cover") }
}

// ---------- "On this page" and the text column ----------

val ArticleLayoutStyle = CssStyle {
    base {
        grid("minmax(0, 1fr)")
            .css("gap" to "clamp(28px, 5vw, 72px)", "align-items" to "start", "padding-block" to "clamp(40px, 5vw, 64px) clamp(56px, 7vw, 88px)")
    }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "250px minmax(0, 1fr)") }
}

/** A folding box above the text on small screens, a sticky list beside it on wide ones. */
val ArticleTocStyle = CssStyle {
    base {
        Modifier.backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(14.px)
            .css("padding" to "14px 18px")
    }
    Breakpoint.LG {
        Modifier.position(Position.Sticky)
            .css(
                "background" to "transparent", "border" to "0", "padding" to "0",
                "top" to "calc(env(safe-area-inset-top, 0px) + 96px)", "max-height" to "calc(100vh - 120px)", "overflow" to "auto",
            )
    }
    cssRule(" summary") {
        Modifier.display(DisplayStyle.Flex).color(RdColor.Muted)
            .css(
                "justify-content" to "space-between", "cursor" to "pointer", "list-style" to "none", "padding" to "4px 0",
                "font" to "500 .72rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".1em", "text-transform" to "uppercase",
            )
    }
    cssRule(" summary::-webkit-details-marker") { Modifier.display(DisplayStyle.None) }
    cssRule("[open] summary") { Modifier.css("padding-bottom" to "12px") }
    cssRule(" ol") {
        Modifier.display(DisplayStyle.Grid)
            .css("list-style" to "none", "margin" to "0", "padding" to "0", "border-left" to "2px solid rgb(220,227,236)")
    }
    cssRule(" a") {
        Modifier.display(DisplayStyle.Block).color(RdColor.Muted).textDecorationLine(TextDecorationLine.None)
            .css("padding" to "7px 0 7px 14px", "margin-left" to "-2px", "border-left" to "2px solid transparent", "font-size" to ".9rem", "line-height" to 1.35)
    }
    cssRule(" a:hover") { Modifier.color(RdColor.Ink) }
    cssRule(" a[aria-current]") { Modifier.color(RdColor.Brand).css("border-left-color" to "rgb(46,75,110)", "font-weight" to 600) }
}

/** On wide screens the list is always open, so its summary is just a label. */
val ArticleTocSummaryStyle = CssStyle {
    Breakpoint.LG { Modifier.css("pointer-events" to "none") }
}

/** The "+" / "−" sign of the folding box, not needed on wide screens. */
val ArticleTocSignStyle = CssStyle {
    base { Modifier.css("font-size" to "1.1rem", "line-height" to ".7") }
    Breakpoint.LG { Modifier.display(DisplayStyle.None) }
}

/**
 * The article text. Element rules use :where() so they stay easy to override, for example by the intro and
 * question styles below; links get an attribute selector to beat the page's plain "p a" rule.
 */
val ArticleProseStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid)
            .css("max-width" to "70ch", "min-width" to "0", "font-size" to "1.1rem", "line-height" to 1.75, "gap" to "44px")
    }
    cssRule(" > section") { Modifier.display(DisplayStyle.Grid).gap(18.px).css("scroll-margin-top" to "96px") }
    cssRule(" :where(h2)") {
        Modifier.fontFamily(*RdFont.DISPLAY)
            .css("font-size" to "clamp(1.5rem, 1.1rem + 1.3vw, 2.05rem)", "font-weight" to 800, "font-stretch" to "108%", "line-height" to 1.15)
    }
    cssRule(" :where(h3)") { Modifier.fontFamily(*RdFont.DISPLAY).css("font-size" to "1.15rem", "font-weight" to 750, "line-height" to 1.3) }
    // Section titles fill the line before wrapping, instead of the site-wide balanced (half-and-half) lines.
    // "> section > h2" is specific enough to beat that site-wide heading rule.
    cssRule(" > section > h2") { Modifier.css("text-wrap" to "wrap") }
    cssRule(" :where(p)") { Modifier.css("color" to PROSE_TEXT) }
    cssRule(" :where(p, li, td) a[href]") {
        Modifier.color(RdColor.Brand).css("font-weight" to 600, "text-underline-offset" to "3px", "text-decoration-thickness" to "1px")
    }
    cssRule(" :where(p, li, td) a[href]:hover") { Modifier.css("text-decoration-thickness" to "2px") }
    cssRule(" :where(ul)") { Modifier.display(DisplayStyle.Grid).gap(10.px).css("list-style" to "none", "margin" to "0", "padding" to "0") }
    cssRule(" :where(ul) > li") { grid("24px 1fr").gap(10.px).css("color" to PROSE_TEXT) }
    cssRule(" :where(ul) > li::before") {
        Modifier.backgroundColor(RdColor.SkySoft).borderRadius(50.px).css(
            "content" to "\"\"", "width" to "22px", "height" to "22px", "margin-top" to "4px", "border" to SKY_LINE,
            "background-image" to "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%232E4B6E' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 12.5 4 4 8-9'/%3E%3C/svg%3E\")",
            "background-position" to "center", "background-size" to "12px", "background-repeat" to "no-repeat",
        )
    }
}

/** The opening section reads as a lede, with its heading as a small label. */
val ArticleIntroStyle = CssStyle {
    cssRule(" h2") {
        Modifier.display(DisplayStyle.Flex).gap(10.px).color(RdColor.Muted)
            .css("align-items" to "center", "font" to "500 .78rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".12em", "text-transform" to "uppercase")
    }
    cssRule(" h2::before") { Modifier.backgroundColor(RdColor.LogoBlue).css("content" to "\"\"", "width" to "22px", "height" to "2px") }
    cssRule(" p") { Modifier.color(RdColor.Ink).css("font-size" to "1.2rem") }
}

/** A numbered heading: the badge, then the title as one block, so wrapped lines line up with the first. */
val ArticleNumberedHeadingStyle = CssStyle {
    base { Modifier.css("display" to "flex", "align-items" to "baseline") }
    cssRule(" > :first-child") { Modifier.css("flex" to "none") }
}

/** The number of a numbered point, in a logo-blue badge before its heading. */
val ArticleNumberStyle = CssStyle.base {
    Modifier.backgroundColor(RdColor.LogoBlue).color(RdColor.Ink).borderRadius(10.px)
        .css("display" to "inline-grid", "place-items" to "center", "min-width" to "1.7em", "height" to "1.7em", "padding-inline" to ".3em", "margin-right" to ".45em", "font-size" to ".72em", "vertical-align" to ".12em")
}

/** A paragraph that is only a link becomes a "learn more" card. */
val ArticleMoreLinkStyle = CssStyle {
    cssRule(" a[href]") {
        Modifier.display(DisplayStyle.Flex).gap(16.px).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(12.px).textDecorationLine(TextDecorationLine.None)
            .css("justify-content" to "space-between", "align-items" to "center", "padding" to "16px 18px")
    }
    cssRule(" a[href]::after") {
        Modifier.display(DisplayStyle.Grid).backgroundColor(RdColor.LogoBlue).color(RdColor.Ink).borderRadius(50.px)
            .css("content" to "\"→\"", "font-family" to RdFont.MONO_STACK, "flex" to "none", "width" to "34px", "height" to "34px", "place-items" to "center")
    }
    cssRule(" a[href]:hover") { Modifier.css("border-color" to "rgb(46,75,110)") }
}

val ArticlePhotoStyle = CssStyle {
    base { Modifier.borderRadius(16.px).css("margin" to "6px 0", "overflow" to "hidden", "max-width" to "100%") }
    cssRule(" img") { Modifier.display(DisplayStyle.Block).css("width" to "100%", "height" to "auto") }
}

/** Screenshots of websites on devices sit on a pale-blue panel, side by side. */
val ArticleDevicesStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).gap(16.px).backgroundColor(RdColor.SkySoft).borderRadius(16.px)
            .css("margin" to "6px 0", "border" to SKY_LINE, "padding" to "clamp(14px, 3vw, 28px)", "grid-auto-flow" to "column", "grid-auto-columns" to "1fr", "align-items" to "end")
    }
    cssRule(" img") { Modifier.display(DisplayStyle.Block).css("width" to "100%", "height" to "auto", "max-height" to "420px", "object-fit" to "contain") }
}

val ArticleTableWrapStyle = CssStyle.base {
    Modifier.backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(14.px).css("overflow-x" to "auto")
}

val ArticleTableStyle = CssStyle {
    base { Modifier.css("width" to "100%", "border-collapse" to "collapse", "font-size" to ".96rem", "line-height" to 1.5) }
    cssRule(" th") {
        Modifier.backgroundColor(RdColor.SkySoft).color(RdColor.Ink)
            .css("text-align" to "left", "font" to "600 .9rem/1.3 ${RdFont.BODY_STACK}", "padding" to "14px 16px", "border-bottom" to SKY_LINE)
    }
    cssRule(" td") { Modifier.css("padding" to "13px 16px", "border-top" to "1px solid rgb(220,227,236)", "color" to PROSE_TEXT, "vertical-align" to "top") }
    cssRule(" tbody tr:first-child td") { Modifier.css("border-top" to "0") }
    // Opaque, so the other columns slide under it when the first column stays put on phones.
    cssRule(" td:first-child") { Modifier.color(RdColor.Ink).backgroundColor(RdColor.Paper).css("font-weight" to 600) }
}

/** Tables with more than two columns keep a minimum width and scroll sideways on phones. */
val ArticleWideTableStyle = CssStyle.base { Modifier.css("min-width" to "620px") }

/** When the table scrolls sideways, i.e. its frame's data-scroll is set and isn't "none". */
private const val TABLE_SCROLLS = "[data-scroll]:not([data-scroll='none'])"

/**
 * On a table frame: while the table scrolls sideways, every column after the fixed first one is exactly as wide as
 * the space beside it, so a cell's text wraps onto more lines instead of running off the screen, and each swipe
 * settles on the next whole column. Widths are in cqw, a share of the scrolling box's width.
 */
val ArticleTableFitStyle = CssStyle {
    cssRule(" > div > div") { Modifier.css("container-type" to "inline-size") }
    cssRule("$TABLE_SCROLLS > div > div") { Modifier.css("scroll-snap-type" to "x mandatory", "scroll-padding-left" to "42%") }
    cssRule("$TABLE_SCROLLS table") { Modifier.css("min-width" to "0") }
    // Extra right padding keeps the text clear of the fade and arrow on the right edge.
    cssRule("$TABLE_SCROLLS :is(th, td)") {
        Modifier.css("box-sizing" to "border-box", "width" to "58cqw", "min-width" to "58cqw", "padding-right" to "32px")
    }
    cssRule("$TABLE_SCROLLS :is(th, td):first-child") {
        Modifier.css("width" to "42cqw", "min-width" to "42cqw", "padding-inline" to "12px", "overflow-wrap" to "anywhere")
    }
    cssRule("$TABLE_SCROLLS thead th:not(:first-child)") { Modifier.css("scroll-snap-align" to "start") }
}

val ArticleQuestionsStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(12.px) }
    cssRule(" > div") {
        Modifier.display(DisplayStyle.Grid).gap(8.px).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(14.px).css("padding" to "18px 20px")
    }
    cssRule(" h3") { Modifier.display(DisplayStyle.Flex).gap(10.px) }
    cssRule(" h3::before") { Modifier.color(RdColor.Brand).css("content" to "\"Q\"", "flex" to "none", "font" to "500 .72rem/1.9 ${RdFont.MONO_STACK}") }
    cssRule(" p") { Modifier.css("font-size" to "1rem") }
}

/** The article's closing section: a pale-blue box with a contact button. */
val ArticleWrapUpStyle = CssStyle {
    base {
        Modifier.backgroundColor(RdColor.SkySoft).borderRadius(20.px)
            .css("border" to SKY_LINE, "padding" to "clamp(22px, 4vw, 36px)")
    }
    cssRule(" p") { Modifier.color(RdColor.Ink) }
    cssRule(" > a:last-child") { Modifier.css("justify-self" to "start", "margin-top" to "6px", "font-weight" to 600) }
}

// ---------- More guides ----------

val ArticleMoreGridStyle = CssStyle {
    base { grid("1fr").gap(20.px) }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "1fr 1fr") }
}

val ArticleMoreCardStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).gap(18.px).backgroundColor(RdColor.Paper).color(RdColor.Ink)
            .border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(16.px).textDecorationLine(TextDecorationLine.None)
            .css("grid-template-columns" to "1fr", "align-items" to "center", "padding" to "16px", "transition" to "border-color .15s, transform .15s")
    }
    Breakpoint.SM { Modifier.css("grid-template-columns" to "150px 1fr") }
    hover { Modifier.css("border-color" to "rgb(30,40,52)", "transform" to "translateY(-2px)") }
    cssRule(" img") {
        Modifier.display(DisplayStyle.Block).borderRadius(10.px)
            .css("width" to "100%", "height" to "auto", "aspect-ratio" to "3 / 2", "object-fit" to "cover")
    }
    cssRule(" h3") { Modifier.fontFamily(*RdFont.DISPLAY).css("font-size" to "1.1rem", "font-weight" to 750, "line-height" to 1.3, "margin" to "0") }
    cssRule(" > div") { Modifier.display(DisplayStyle.Grid).gap(8.px).css("min-width" to "0") }
}
